import { useRef } from "react";
import { Link } from "react-router";
import { Check, ChevronRight, Hourglass, Lock, LockOpen, Target, TrendingUp } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { useTaskBurst } from "@/components/TaskBurst";
import { Badge, Card, cx, PageLoader, SectionTitle } from "@/components/ui";
import { Avatar, Confetti, Points, ProgressBar, ProgressRing } from "@/components/bits";
import { api } from "@/lib/api";
import { greeting } from "@/lib/format";
import { useAction, useKidJobs, useKidResponsibilities, useKidSummary } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { formatHHMM } from "@shared/time";
import type { KidResponsibilityDTO } from "@shared/types";
import { JobCard, useMeaningful, useUnlockCelebration } from "./common";

export function ResponsibilityRow({ item }: { item: KidResponsibilityDTO }) {
  const r = item.responsibility;
  const status = item.completion?.status;
  const checked = status === "done" || status === "approved";
  const burst = useTaskBurst();
  const mark = useRef<HTMLSpanElement>(null);
  const complete = useAction(() => api.post(`/responsibilities/${r.id}/complete`));
  const undo = useAction(() => api.post(`/responsibilities/${r.id}/uncomplete`));
  const busy = complete.isPending || undo.isPending;
  // Show the tap right away; both mutations stay pending until the fresh list has loaded.
  const showChecked = complete.isPending || (checked && !undo.isPending);
  return (
    <button
      type="button"
      aria-pressed={checked}
      disabled={busy || status === "approved"}
      onClick={() => {
        if (checked) undo.mutate();
        else {
          burst(mark.current!);
          complete.mutate();
        }
      }}
      className={cx(
        "flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition active:scale-[0.99]",
        showChecked ? "bg-brand-soft" : "bg-sunken hover:bg-line/50",
      )}
    >
      <span
        ref={mark}
        className={cx(
          "flex size-9 shrink-0 items-center justify-center rounded-xl transition",
          showChecked ? "bg-brand text-on-brand" : "bg-card ring-2 ring-line",
        )}
      >
        {showChecked ? <Check className="size-5" strokeWidth={3} /> : <span className="text-xl">{r.icon}</span>}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cx("block font-extrabold", showChecked && "text-brand-strong")}>{r.title}</span>
        <span className="block text-xs font-semibold text-muted">
          {status === "rejected"
            ? `Not quite — ${item.completion?.note || "take another look"}`
            : status === "done" && !item.counts
              ? "Waiting for a parent to check"
              : status === "done"
                ? "Done! Tap to undo"
                : r.frequency === "weekly"
                  ? "This week"
                  : r.dueTime
                    ? `By ${formatHHMM(r.dueTime)}`
                    : r.description || "Today"}
        </span>
      </span>
      {status === "done" && !item.counts && <Hourglass className="size-4 text-warn" />}
      {status === "rejected" && <Badge tone="danger">Redo</Badge>}
      {!showChecked && status !== "rejected" && (
        <span className="flex shrink-0 items-center gap-1 rounded-full border-2 border-dashed border-brand px-2.5 py-1 text-xs font-black text-brand-strong">
          <Check className="size-3.5" strokeWidth={3} /> Done?
        </span>
      )}
    </button>
  );
}

export function KidToday() {
  const { user, family } = useSession();
  const summary = useKidSummary(user.id);
  const resp = useKidResponsibilities();
  const jobs = useKidJobs();
  const meaningful = useMeaningful();
  const gate = resp.data?.gate;
  const celebrate = useUnlockCelebration(gate);

  if (summary.isLoading || resp.isLoading || jobs.isLoading || !summary.data || !resp.data || !jobs.data) return <PageLoader />;

  const s = summary.data;
  const due = resp.data.items.filter((i) => i.due);
  const notDue = resp.data.items.filter((i) => !i.due);
  const regularJobs = jobs.data.jobs.filter((j) => !j.workItOff);
  const recoveryJobs = jobs.data.jobs.filter((j) => j.workItOff);
  const inProgress = jobs.data.jobs.filter((j) => j.activeRun);
  const openJobs = regularJobs.filter((j) => !j.activeRun && (j.available || j.locked));
  const maxReward = openJobs.reduce((m, j) => Math.max(m, j.reward), 0);
  const equivalent = meaningful.describe(s.available);
  const goal = s.primaryGoal;

  return (
    <div>
      {celebrate > 0 && <Confetti key={celebrate} />}
      <PageHeader
        title={`${greeting()}, ${user.name}!`}
        subtitle={family.name}
        bell="/kid/notifications"
        actions={
          <Link to="/kid/me" className="md:hidden">
            <Avatar member={user} />
          </Link>
        }
      />

      {/* Balance */}
      <Card className="mt-3 overflow-hidden bg-gradient-to-br from-brand to-brand-strong p-5 text-on-brand ring-0">
        <div className="text-sm font-bold opacity-85">My {family.currencyName}</div>
        <div className="mt-1 flex items-end gap-3">
          <Points value={s.balance} family={family} className="text-5xl" />
        </div>
        <div className="mt-1 text-sm font-semibold opacity-90">
          {equivalent ? `That's about ${equivalent}` : "Finish jobs to start earning!"}
          {s.pendingSpend > 0 && ` · ${s.pendingSpend} on hold`}
        </div>
        {s.earnedThisWeek > 0 && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-sm font-bold">
            <TrendingUp className="size-4" /> +{s.earnedThisWeek} this week
          </div>
        )}
        {goal && (
          <Link to="/kid/goals" className="mt-4 block rounded-2xl bg-white/15 p-3 transition hover:bg-white/25">
            <div className="flex items-center justify-between text-sm font-bold">
              <span className="flex items-center gap-1.5">
                <Target className="size-4" /> {goal.icon} {goal.name}
              </span>
              <span>{Math.round(goal.progress * 100)}%</span>
            </div>
            <ProgressBar value={goal.progress} color="#ffd36e" className="mt-2 bg-white/25 ring-0" />
          </Link>
        )}
      </Card>

      {s.balance < 0 && (
        <Card className="mt-3 bg-violet-soft ring-violet/30">
          <div className="font-extrabold text-violet">You're below zero ({s.balance}).</div>
          <p className="mt-1 text-sm text-ink">
            {family.workItOffEnabled
              ? recoveryJobs.length
                ? "You can work it off! Recovery jobs below help you get back to zero."
                : "Earn it back with jobs and good choices — you've got this."
              : "Jobs you finish will help you get back above zero."}
          </p>
        </Card>
      )}

      {/* Responsibilities */}
      <SectionTitle>First, my responsibilities</SectionTitle>
      <Card>
        {due.length === 0 ? (
          <div className="flex items-center gap-3 py-2">
            <span className="text-3xl">🎉</span>
            <div>
              <div className="font-extrabold">Nothing due today</div>
              <div className="text-sm text-muted">Jobs are open — go earn!</div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-4">
              <ProgressRing value={gate!.total ? gate!.done / gate!.total : 1} size={64}>
                <span className="text-sm font-black">
                  {gate!.done}/{gate!.total}
                </span>
              </ProgressRing>
              <div>
                <div className="text-lg font-extrabold">
                  {gate!.unlocked ? "All done — jobs unlocked!" : `${gate!.total - gate!.done} to go`}
                </div>
                <div className="text-sm text-muted">
                  {gate!.unlocked ? "Nice work handling your obligations." : "Tap each one when you've done it, then go earn."}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {due.map((i) => (
                <ResponsibilityRow key={i.responsibility.id} item={i} />
              ))}
            </div>
          </>
        )}
        {notDue.length > 0 && (
          <p className="mt-3 text-xs font-semibold text-faint">
            Not due today: {notDue.map((i) => `${i.responsibility.icon} ${i.responsibility.title}`).join(" · ")}
          </p>
        )}
      </Card>

      {/* In progress */}
      {inProgress.length > 0 && (
        <>
          <SectionTitle>Working on</SectionTitle>
          <div className="space-y-2">
            {inProgress.map((j) => (
              <JobCard key={j.id} job={j} compact />
            ))}
          </div>
        </>
      )}

      {recoveryJobs.filter((j) => !j.activeRun).length > 0 && (
        <>
          <SectionTitle>Work it off</SectionTitle>
          <div className="space-y-2">
            {recoveryJobs
              .filter((j) => !j.activeRun)
              .map((j) => (
                <JobCard key={j.id} job={j} compact />
              ))}
          </div>
        </>
      )}

      {/* Jobs teaser */}
      <SectionTitle
        action={
          <Link to="/kid/jobs" className="flex items-center text-sm font-bold text-brand">
            All jobs <ChevronRight className="size-4" />
          </Link>
        }
      >
        Then, go earn
      </SectionTitle>
      {openJobs.length === 0 ? (
        <Card className="text-center">
          <div className="text-3xl">💡</div>
          <div className="mt-1 font-extrabold">No open jobs right now</div>
          <p className="text-sm text-muted">Have an idea? Pitch your own job!</p>
          <Link to="/kid/jobs" className="mt-3 inline-block font-bold text-brand">
            Pitch a job →
          </Link>
        </Card>
      ) : (
        <>
          <div
            className={cx(
              "mb-2 flex items-center gap-3 rounded-3xl p-4",
              gate!.unlocked ? "bg-coin-soft text-coin-ink" : "bg-locked-soft text-locked",
            )}
          >
            {gate!.unlocked ? <LockOpen className="size-6 shrink-0" /> : <Lock className="size-6 shrink-0" />}
            <div className="text-sm font-bold">
              {gate!.unlocked ? (
                <>
                  {openJobs.length} job{openJobs.length === 1 ? "" : "s"} open — up to{" "}
                  <Points value={maxReward} family={family} /> each!
                </>
              ) : (
                <>
                  {openJobs.length} job{openJobs.length === 1 ? "" : "s"} waiting — worth up to <Points value={maxReward} family={family} />.
                  Finish {gate!.total - gate!.done} more responsibilit{gate!.total - gate!.done === 1 ? "y" : "ies"} to unlock.
                </>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {openJobs.slice(0, 3).map((j) => (
              <JobCard key={j.id} job={j} compact />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
